/**
 * Platform Admin API Service
 *
 * Handles platform-level operations: tenants, organizations,
 * module management, demo org seeding.
 */
import apiClient from './client';
import type { ModuleRegistryItem, OrgUser, Department } from './org';

// ═══ Types ═══

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: string;
  primary_org_id: string | null;
  created_at: string;
  org_count?: number;
  user_count?: number;
}

export interface PlatformOrganization {
  id: string;
  tenant_id: string;
  name: string;
  country: string | null;
  org_type: 'parent' | 'subsidiary';
  parent_org_id: string | null;
  billing_mode: 'self' | 'sponsored';
  status: string;
  created_at: string;
  user_count?: number;
}

export interface OrgModuleAccess {
  id: string;
  org_id: string;
  module_id: string;
  enabled: boolean;
  activated_at: string | null;
}

export interface DemoSeedRequest {
  org_name: string;
}

export interface DemoSeedResponse {
  org_id: string;
  org_name: string;
  demo_email: string;
  demo_password: string;
  user_count: number;
  kpi_count: number;
}

export interface DemoOrgListItem {
  org_id: string;
  org_name: string;
  demo_email: string;
  demo_password: string | null;
  user_count: number;
  created_at: string | null;
}

// ═══ Tenant Management ═══

export const listTenants = async (): Promise<Tenant[]> => {
  const { data } = await apiClient.get('/api/platform/tenants');
  return data;
};

export const createTenant = async (payload: {
  name: string;
  slug: string;
}): Promise<Tenant> => {
  const { data } = await apiClient.post('/api/platform/tenants', payload);
  return data;
};

export const updateTenant = async (
  tenantId: string,
  payload: { name?: string; status?: string }
): Promise<Tenant> => {
  const { data } = await apiClient.patch(`/api/platform/tenants/${tenantId}`, payload);
  return data;
};

export const listTenantOrganizations = async (
  tenantId: string
): Promise<PlatformOrganization[]> => {
  const { data } = await apiClient.get(`/api/platform/tenants/${tenantId}/organizations`);
  return data;
};

// ═══ Organization Management ═══

export const listAllOrganizations = async (
  tenantId?: string
): Promise<PlatformOrganization[]> => {
  const params = tenantId ? { tenant_id: tenantId } : {};
  const { data } = await apiClient.get('/api/platform/organizations', { params });
  return data;
};

export const createOrganization = async (
  tenantId: string,
  payload: { name: string; country?: string; org_type?: string; billing_mode?: string }
): Promise<PlatformOrganization> => {
  const { data } = await apiClient.post(
    `/api/platform/organizations?tenant_id=${tenantId}`,
    payload
  );
  return data;
};

export const updateOrganization = async (
  orgId: string,
  payload: { name?: string; status?: string; country?: string }
): Promise<PlatformOrganization> => {
  const { data } = await apiClient.patch(`/api/platform/organizations/${orgId}`, payload);
  return data;
};

// ═══ Module Management ═══

export const listOrgModules = async (orgId: string): Promise<OrgModuleAccess[]> => {
  const { data } = await apiClient.get(`/api/platform/organizations/${orgId}/modules`);
  return data;
};

export const toggleOrgModule = async (
  orgId: string,
  moduleId: string,
  enabled: boolean,
  provisionUsers: 'all' | 'none' = 'all'
): Promise<OrgModuleAccess> => {
  const { data } = await apiClient.put(`/api/platform/organizations/${orgId}/modules`, {
    module_id: moduleId,
    enabled,
    provision_users: provisionUsers,
  });
  return data;
};

export const getModuleRegistry = async (): Promise<ModuleRegistryItem[]> => {
  const { data } = await apiClient.get('/api/platform/modules');
  return data;
};

// ═══ User & Department Management ═══

export const listPlatformOrgUsers = async (orgId: string): Promise<OrgUser[]> => {
  const { data } = await apiClient.get(`/api/platform/organizations/${orgId}/users`);
  return data;
};

export const listPlatformOrgDepartments = async (orgId: string): Promise<Department[]> => {
  const { data } = await apiClient.get(`/api/platform/organizations/${orgId}/departments`);
  return data;
};

// ═══ User Invitations ═══

export const platformInviteUser = async (
  orgId: string,
  invite: {
    email: string;
    display_name?: string;
    title?: string;
    system_role?: string;
    department_id?: string;
    reports_to?: string;
    module_access?: string[];
    module_roles?: Record<string, string>;
  }
): Promise<OrgUser> => {
  const { data } = await apiClient.post(
    `/api/platform/organizations/${orgId}/users/invite`,
    invite
  );
  return data;
};

// ═══ User Search (impersonation) ═══

export interface UserSearchResult {
  id: string;
  email: string;
  display_name: string | null;
  org_name: string | null;
  system_role: string;
}

export const searchPlatformUsers = async (q: string): Promise<UserSearchResult[]> => {
  const { data } = await apiClient.get('/api/platform/users/search', { params: { q } });
  return data;
};

// ═══ Demo Organizations ═══

export const seedDemoOrg = async (data: DemoSeedRequest): Promise<DemoSeedResponse> => {
  const { data: resp } = await apiClient.post('/api/platform/demo/seed', data);
  return resp;
};

export const listDemoOrgs = async (): Promise<DemoOrgListItem[]> => {
  const { data } = await apiClient.get('/api/platform/demo/orgs');
  return data;
};

export const deleteDemoOrg = async (orgId: string): Promise<void> => {
  await apiClient.delete(`/api/platform/demo/${orgId}`);
};

// ═══ Platform LLM API Key ═══

export interface LlmApiKeyStatus {
  configured: boolean;
  hint: string | null;
  using_platform_default: boolean;
}

export const getPlatformLlmApiKey = async (): Promise<LlmApiKeyStatus> => {
  const { data } = await apiClient.get('/api/platform/llm-api-key');
  return data;
};

export const setPlatformLlmApiKey = async (apiKey: string): Promise<LlmApiKeyStatus> => {
  const { data } = await apiClient.put('/api/platform/llm-api-key', { api_key: apiKey });
  return data;
};

export const clearPlatformLlmApiKey = async (): Promise<LlmApiKeyStatus> => {
  const { data } = await apiClient.delete('/api/platform/llm-api-key');
  return data;
};

export interface LlmModelStatus {
  model: string;
}

export const getPlatformLlmModel = async (): Promise<LlmModelStatus> => {
  const { data } = await apiClient.get('/api/platform/llm-model');
  return data;
};

export const setPlatformLlmModel = async (model: string): Promise<LlmModelStatus> => {
  const { data } = await apiClient.put('/api/platform/llm-model', { model });
  return data;
};
